import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, collection, query, where, getDocs, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import HowItWorksModal from '../components/HowItWorksModal';
import { useTranslation } from 'react-i18next';

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@400;500;600;700&family=Inter:wght@300;400;500;600&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #080810; color: #F5F5F0; font-family: 'Inter', sans-serif; }
  @keyframes fadeIn { from { opacity:0; transform:translateY(24px); } to { opacity:1; transform:translateY(0); } }
  @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-18px)} }
  @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }
  @keyframes shimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
  .group-card {
    transition: all 0.25s ease;
    cursor: pointer;
  }
  .group-card:hover {
    transform: translateY(-4px);
    border-color: rgba(240,180,41,0.5) !important;
    box-shadow: 0 20px 40px rgba(0,0,0,0.4), 0 0 0 1px rgba(240,180,41,0.15) !important;
  }
  .group-card:active { transform: translateY(-1px); }
  .btn-logout:hover { background: rgba(255,255,255,0.08) !important; }
  .skeleton {
    background: linear-gradient(90deg, rgba(255,255,255,0.04) 25%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.04) 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
    border-radius: 8px;
  }
`;

const C = {
  gold: '#F0B429', goldDark: '#C9820A',
  bg: '#080810', card: '#14141f',
  border: 'rgba(255,255,255,0.08)',
  green: '#06B86A', red: '#E63946',
};

export default function MisGruposPage() {
  const { t } = useTranslation();
  const { currentUser, userData, logout } = useAuth();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mousePos, setMousePos] = useState({ x: 50, y: 50 });
  const [showHowItWorks, setShowHowItWorks] = useState(false);

useEffect(() => {
    const handleMouseMove = e => setMousePos({ x: (e.clientX / window.innerWidth) * 100, y: (e.clientY / window.innerHeight) * 100 });
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    if (currentUser && userData) {
      loadGroups();
    } else if (currentUser && !userData) {
      // userData puede llegar tarde (Google login) — reintentar
      const timer = setTimeout(() => loadGroups(), 800);
      return () => clearTimeout(timer);
    }
  }, [currentUser, userData]);

  async function loadGroups() {
    if (!currentUser) return;
    setLoading(true);
    try {
      const uid = currentUser.uid;
      const results = [];
      const adminQuery = query(collection(db, 'groups'), where('adminId', '==', uid));
      const adminSnap = await getDocs(adminQuery);
      for (const d of adminSnap.docs) {
        results.push({ id: d.id, ...d.data(), myRole: 'admin' });
      }
      // Si userData no llegó aún, leerlo directo de Firestore
      let uData = userData;
      if (!uData) {
        const snap = await getDoc(doc(db, 'users', uid));
        uData = snap.exists() ? snap.data() : {};
      }
      const groupIds = uData.groupIds || (uData.groupId ? [uData.groupId] : []);
      for (const gid of groupIds) {
        if (!results.find(r => r.id === gid)) {
          const snap = await getDoc(doc(db, 'groups', gid));
          if (snap.exists()) {
            results.push({ id: snap.id, ...snap.data(), myRole: 'participant' });
          }
        } else {
          const existing = results.find(r => r.id === gid);
          if (existing) existing.alsoParticipant = true;
        }
      }
      setGroups(results);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }

  function enterGroup(group, asParticipant = false) {
    localStorage.setItem('activeGroupId', group.id);
    if (asParticipant) {
      navigate('/dashboard/' + group.id);
    } else if (group.myRole === 'admin') {
      navigate('/admin');
    } else {
      navigate('/dashboard/' + group.id);
    }
  }

  async function handleLogout() {
    await logout();
    navigate('/');
  }

  async function handleDeleteGroup(e, groupId) {
    e.stopPropagation();
    if (window.confirm(t('delete_confirm'))) {
      try {
        await deleteDoc(doc(db, 'groups', groupId));
        if (localStorage.getItem('activeGroupId') === groupId) {
          localStorage.removeItem('activeGroupId');
        }
        loadGroups();
      } catch (error) {
        console.error("Error eliminando grupo:", error);
        alert(t('delete_error'));
      }
    }
  }

  const adminGroups = groups.filter(g => g.myRole === 'admin');
  const participantGroups = groups.filter(g => g.myRole === 'participant' || g.alsoParticipant === true);

  return (
    <div style={{ minHeight: '100vh', background: C.bg, position: 'relative', overflow: 'hidden', padding: '24px 20px 40px' }}>
      <style>{CSS}</style>

<div style={{ position: 'fixed', inset: 0, opacity: 0.35, background: `radial-gradient(circle at ${mousePos.x}% ${mousePos.y}%, rgba(240,180,41,0.25) 0%, transparent 60%)`, transition: 'background 0.4s ease', pointerEvents: 'none' }} />

      <div style={{ position: 'relative', zIndex: 10, maxWidth: 560, margin: '0 auto' }}>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 36, animation: 'fadeIn 0.4s ease' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: 14, background: `linear-gradient(135deg, ${C.gold}, ${C.goldDark})`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 6px 20px rgba(240,180,41,0.3)', flexShrink: 0 }}>
              <span style={{ fontSize: 24 }}>🏆</span>
            </div>
            <div>
              <h1 style={{ fontFamily: "'Oswald',sans-serif", fontSize: 22, fontWeight: 700, letterSpacing: 1, lineHeight: 1 }}>
                PITAZO <span style={{ color: C.gold }}>MUNDIAL</span>
              </h1>
              <p style={{ fontSize: 12, color: '#6B6B7B', marginTop: 3 }}>
                {t('hello')}, <span style={{ color: '#aaa' }}>{userData?.name || currentUser?.email}</span>
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={() => setShowHowItWorks(true)} style={{ background: 'rgba(240,180,41,0.08)', border: '1px solid rgba(240,180,41,0.2)', borderRadius: 10, width: 36, height: 36, color: '#F0B429', fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>?</button>
            <button className="btn-logout" onClick={handleLogout} style={{ background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 10, padding: '8px 14px', color: '#6B6B7B', fontFamily: "'Inter',sans-serif", fontSize: 12, cursor: 'pointer', transition: 'all 0.2s' }}>{t('logout')}</button>
          </div>
        </div>

        {loading ? (
          <div style={{ animation: 'fadeIn 0.3s ease' }}>
            {[1,2,3].map(i => (
              <div key={i} className="skeleton" style={{ height: 80, marginBottom: 12 }} />
            ))}
          </div>
        ) : (
          <>
            {adminGroups.length > 0 && (
              <div style={{ marginBottom: 32, animation: 'fadeIn 0.5s ease' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                  <span style={{ fontSize: 16 }}>👑</span>
                  <span style={{ fontFamily: "'Oswald',sans-serif", fontSize: 13, fontWeight: 600, color: C.gold, letterSpacing: 1.5, textTransform: 'uppercase' }}>{t('my_groups_admin')}</span>
                  <div style={{ flex: 1, height: 1, background: `linear-gradient(to right, rgba(240,180,41,0.3), transparent)` }} />
                </div>
                {adminGroups.map((g, i) => (
                  <GroupCard key={g.id} group={g} onEnter={() => enterGroup(g)} delay={i * 80} onDelete={(e) => handleDeleteGroup(e, g.id)} t={t} />
                ))}
              </div>
            )}

            {participantGroups.length > 0 && (
              <div style={{ marginBottom: 32, animation: 'fadeIn 0.6s ease' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                  <span style={{ fontSize: 16 }}>🎯</span>
                  <span style={{ fontFamily: "'Oswald',sans-serif", fontSize: 13, fontWeight: 600, color: '#8B8B9B', letterSpacing: 1.5, textTransform: 'uppercase' }}>{t('my_groups_participant')}</span>
                  <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.06)' }} />
                </div>
                {participantGroups.map((g, i) => (
                  <GroupCard key={g.id} group={g} onEnter={() => enterGroup(g, true)} delay={i * 80} isParticipantView={true} t={t} />
                ))}
              </div>
            )}

            {groups.length === 0 && (
              <div style={{ textAlign: 'center', padding: '60px 20px', animation: 'fadeIn 0.5s ease' }}>
                <div style={{ fontSize: 56, marginBottom: 16 }}>🏟</div>
                <p style={{ fontFamily: "'Oswald',sans-serif", fontSize: 18, color: '#6B6B7B', letterSpacing: 1 }}>{t('no_groups_yet')}</p>
                <p style={{ fontSize: 13, color: '#444', marginTop: 8 }}>{t('no_groups_subtitle')}</p>
                <button onClick={() => navigate('/crear-grupo')} style={{ marginTop: 24, background: `linear-gradient(135deg, ${C.gold}, ${C.goldDark})`, border: 'none', borderRadius: 12, padding: '13px 28px', color: C.bg, fontFamily: "'Oswald',sans-serif", fontSize: 14, fontWeight: 700, letterSpacing: 1, cursor: 'pointer' }}>{t('create_group_btn')}</button>
              </div>
            )}

            {groups.length > 0 && (
              <div style={{ marginTop: 8, animation: 'fadeIn 0.7s ease' }}>
                <button onClick={() => navigate('/crear-grupo')}
                  style={{ width: '100%', padding: '14px', background: 'transparent', border: `1px dashed rgba(240,180,41,0.3)`, borderRadius: 16, color: C.gold, fontFamily: "'Oswald',sans-serif", fontSize: 14, fontWeight: 600, letterSpacing: 1, cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  {t('add_new_group')}
                </button>
              </div>
            )}
          </>
        )}

        {/* Cómo funciona */}
        <div style={{ width: '100%', marginTop: 32, animation: 'fadeIn 0.5s ease 0.3s both' }}>
          <div style={{ fontSize: 10, color: '#444', letterSpacing: 2, textAlign: 'center', marginBottom: 14 }}>{t('how_it_works_label').toUpperCase()}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[
              { icon: '👥', text: t('step_1') },
              { icon: '📲', text: t('step_2') },
              { icon: '⚽', text: t('step_3') },
              { icon: '🏆', text: t('step_4') },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '12px 16px' }}>
                <span style={{ fontSize: 20, flexShrink: 0 }}>{item.icon}</span>
                <span style={{ fontSize: 13, color: '#aaa', lineHeight: 1.4 }}>{item.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Países sede */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 24, marginTop: 24 }}>
          {[{ flag: '🇺🇸', name: 'USA' }, { flag: '🇨🇦', name: 'CAN' }, { flag: '🇲🇽', name: 'MEX' }].map((p, i) => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 26 }}>{p.flag}</div>
              <div style={{ fontSize: 9, color: '#555', letterSpacing: 1, marginTop: 3 }}>{p.name}</div>
            </div>
          ))}
        </div>

        {/* ADMOB_INTERSTITIAL: mostrar aquí al navegar entre grupos */}

        {/* Footer */}
        <div style={{ marginTop: 28, fontSize: 10, color: '#333', letterSpacing: 1, textAlign: 'center' }}>
          © 2026 Pitazo Mundial ·{' '}
          <span style={{ color: '#444', cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => window.open('https://pitazomundial.com/privacidad.html', '_blank')}>
            {t('privacy')}
          </span>
        </div>

        {/* Disclaimer AdMob */}
        <div style={{ marginTop: 10, fontSize: 10, color: '#2a2a3a', letterSpacing: 0.5, textAlign: 'center', lineHeight: 1.6, padding: '0 8px' }}>
          Pitazo Mundial es una plataforma de entretenimiento para predicción de resultados deportivos. No gestiona apuestas ni dinero real entre usuarios. Los pagos corresponden únicamente a la ampliación del número de participantes por grupo.
        </div>
      </div>
      {showHowItWorks && <HowItWorksModal onClose={() => setShowHowItWorks(false)} />}
    </div>
  );
}

function GroupCard({ group, onEnter, delay = 0, isParticipantView = false, onDelete, t }) {
  const isAdmin = group.myRole === 'admin' && !isParticipantView;
  const statusText = group.status === 'active' ? t('status_active') : t('status_pending');

  return (
    <div className="group-card" onClick={onEnter}
      style={{
        background: C.card,
        border: `1px solid ${isAdmin ? 'rgba(240,180,41,0.2)' : C.border}`,
        borderRadius: 16,
        padding: '18px 20px',
        marginBottom: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        animation: `fadeIn 0.4s ease ${delay}ms both`,
        boxShadow: isAdmin ? '0 4px 20px rgba(240,180,41,0.08)' : 'none',
      }}>

      <div style={{
        width: 48, height: 48, borderRadius: 14, flexShrink: 0,
        background: isAdmin
          ? `linear-gradient(135deg, ${C.gold}, ${C.goldDark})`
          : 'rgba(255,255,255,0.05)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 22,
        boxShadow: isAdmin ? '0 4px 12px rgba(240,180,41,0.25)' : 'none'
      }}>
        {isAdmin ? '🏆' : '🎯'}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 17, fontWeight: 600, letterSpacing: 0.5, color: '#F5F5F0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {group.name || 'Sin nombre'}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 5, flexWrap: 'wrap' }}>
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: 1,
            padding: '2px 8px', borderRadius: 20,
            background: isAdmin ? 'rgba(240,180,41,0.15)' : 'rgba(255,255,255,0.06)',
            color: isAdmin ? C.gold : '#6B6B7B',
            textTransform: 'uppercase'
          }}>
            {isAdmin ? t('role_admin') : t('role_participant')}
          </span>

          {isAdmin && (
            <span style={{ fontSize: 11, color: group.status === 'active' ? C.green : C.gold }}>
              {statusText}
            </span>
          )}

          {group.alsoParticipant && (
            <span style={{ fontSize: 10, color: '#555', letterSpacing: 0.5 }}>{t('also_participant')}</span>
          )}
          {group.currentMembers > 0 && (
            <span style={{ fontSize: 11, color: '#555' }}>· {group.currentMembers} ptcs</span>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {isAdmin && onDelete && (
          <button 
            onClick={onDelete}
            style={{
              background: 'rgba(230,57,70,0.1)', 
              border: '1px solid rgba(230,57,70,0.3)',
              borderRadius: 8, 
              width: 34, 
              height: 34,
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              cursor: 'pointer',
              zIndex: 2
            }}
          >
            🗑️
          </button>
        )}
        <div style={{ color: isAdmin ? C.gold : '#444', fontSize: 18, flexShrink: 0, transition: 'transform 0.2s' }}>→</div>
      </div>
    </div>
  );
}